import { useEffect, useRef, useState } from "react";
import type { StreamControls, StreamThumbnail } from "@shared/player/controls";

/**
 * The thumbnail for a scrub target, fetched only while the target holds still.
 *
 * Deliberately NOT per press. `getThumbnails` is a network fetch of a sprite
 * sheet, and a guest holding right at 6 Hz would queue dozens of them for
 * positions they have already passed - all of which would land, out of order,
 * after the seek had committed. So it waits for the target to settle, and the
 * last request wins by generation number rather than by arrival time.
 */
export function useScrubThumbnail(
  controls: StreamControls | null,
  scrubbing: boolean,
  target: number,
  settleMs = 180
): StreamThumbnail | null {
  const [thumb, setThumb] = useState<StreamThumbnail | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    if (!scrubbing || !controls) {
      setThumb(null);
      return;
    }
    const mine = ++generation.current;
    const timer = setTimeout(() => {
      void controls.thumbnail(target).then((next) => {
        // A response for a target the guest has already left is not a
        // thumbnail, it is a wrong picture with confident framing.
        if (generation.current === mine) setThumb(next);
      });
    }, settleMs);
    return () => clearTimeout(timer);
  }, [controls, scrubbing, target, settleMs]);

  return thumb;
}
